import { cn } from "../utils/cn";

// ── Types ─────────────────────────────────────────────────────────────────────

export type SkeletonVariant =
  | "text"
  | "circle"
  | "rect"
  | "card"
  | "route-card"
  | "operator-card"
  | "stat-card"
  | "table-row";

export interface SkeletonLoaderProps {
  variant: SkeletonVariant;
  width?: string;
  height?: string;
  /** Render N skeleton instances */
  count?: number;
  className?: string;
}

// ── Base shimmer box ──────────────────────────────────────────────────────────

function Bone({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      aria-hidden="true"
      className={cn("skeleton-shimmer rounded", className)}
      style={style}
    />
  );
}

// ── Individual variants ───────────────────────────────────────────────────────

function TextSkeleton({ width, height }: { width?: string; height?: string }) {
  return (
    <Bone
      className="w-full"
      style={{ width, height: height ?? "1rem" }}
    />
  );
}

function CircleSkeleton({ width, height }: { width?: string; height?: string }) {
  const size = width ?? height ?? "2.5rem";
  return (
    <Bone
      className="rounded-full shrink-0"
      style={{ width: size, height: size }}
    />
  );
}

function RectSkeleton({ width, height }: { width?: string; height?: string }) {
  return (
    <Bone
      className="rounded-md"
      style={{ width: width ?? "100%", height: height ?? "5rem" }}
    />
  );
}

function CardSkeleton() {
  return (
    <div className="rounded-card overflow-hidden bg-white dark:bg-surface shadow-sm dark:ring-1 dark:ring-white/5 p-4 space-y-3">
      <Bone className="w-full h-40 rounded-lg" />
      <Bone className="w-3/4 h-5" />
      <Bone className="w-full h-4" />
      <Bone className="w-2/3 h-4" />
    </div>
  );
}

function RouteCardSkeleton() {
  return (
    <div className="rounded-card overflow-hidden bg-white dark:bg-surface shadow-sm dark:ring-1 dark:ring-white/5">
      {/* Image area */}
      <Bone className="w-full h-44 rounded-none" />

      <div className="p-4 space-y-2.5">
        {/* Title */}
        <Bone className="w-4/5 h-5" />
        {/* Location */}
        <Bone className="w-2/3 h-4" />
        {/* Rating row */}
        <div className="flex items-center gap-3">
          <Bone className="w-16 h-4" />
          <Bone className="w-20 h-4" />
        </div>
        {/* Tags */}
        <div className="flex gap-2 pt-0.5">
          <Bone className="w-14 h-5 rounded-full" />
          <Bone className="w-16 h-5 rounded-full" />
        </div>
        {/* Divider + price */}
        <div className="flex items-center justify-between pt-1">
          <Bone className="w-24 h-5" />
          <Bone className="w-16 h-7 rounded-md" />
        </div>
      </div>
    </div>
  );
}

function OperatorCardSkeleton() {
  return (
    <div className="rounded-card p-4 bg-white dark:bg-surface shadow-sm dark:ring-1 dark:ring-white/5 space-y-3">
      {/* Header row: avatar + name/location */}
      <div className="flex items-start gap-3">
        <Bone className="w-12 h-12 rounded-full shrink-0" />
        <div className="flex-1 space-y-1.5 pt-0.5">
          <Bone className="w-3/4 h-4" />
          <Bone className="w-1/2 h-3.5" />
        </div>
      </div>
      {/* Rating */}
      <Bone className="w-28 h-4" />
      {/* Bio lines */}
      <Bone className="w-full h-3.5" />
      <Bone className="w-5/6 h-3.5" />
      {/* Tag pills */}
      <div className="flex gap-2 pt-0.5">
        <Bone className="w-16 h-6 rounded-full" />
        <Bone className="w-20 h-6 rounded-full" />
        <Bone className="w-14 h-6 rounded-full" />
      </div>
    </div>
  );
}

function StatCardSkeleton() {
  return (
    <div className="rounded-card p-5 bg-white dark:bg-surface shadow-sm dark:ring-1 dark:ring-white/5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 space-y-2.5">
          <Bone className="w-20 h-3.5" />
          <Bone className="w-28 h-8 rounded-md" />
          <Bone className="w-32 h-3.5" />
        </div>
        <Bone className="w-11 h-11 rounded-xl shrink-0" />
      </div>
    </div>
  );
}

function TableRowSkeleton() {
  return (
    <div className="flex items-center gap-4 px-4 py-3.5 border-b border-gray-100 dark:border-border-strong">
      <Bone className="w-4 h-4 shrink-0" />
      <Bone className="flex-1 h-4" style={{ maxWidth: "35%" }} />
      <Bone className="w-24 h-4" />
      <Bone className="w-20 h-4" />
      <Bone className="w-16 h-4" />
      <Bone className="w-6 h-6 rounded-md ml-auto" />
    </div>
  );
}

// ── Dispatcher ────────────────────────────────────────────────────────────────

function SkeletonSingle({
  variant,
  width,
  height,
}: {
  variant: SkeletonVariant;
  width?: string;
  height?: string;
}) {
  switch (variant) {
    case "text":           return <TextSkeleton width={width} height={height} />;
    case "circle":         return <CircleSkeleton width={width} height={height} />;
    case "rect":           return <RectSkeleton width={width} height={height} />;
    case "card":           return <CardSkeleton />;
    case "route-card":     return <RouteCardSkeleton />;
    case "operator-card":  return <OperatorCardSkeleton />;
    case "stat-card":      return <StatCardSkeleton />;
    case "table-row":      return <TableRowSkeleton />;
  }
}

// ── Export ────────────────────────────────────────────────────────────────────

export function SkeletonLoader({
  variant,
  width,
  height,
  count = 1,
  className,
}: SkeletonLoaderProps) {
  const isInline = variant === "text" || variant === "circle" || variant === "rect";
  const isRow    = variant === "table-row";

  if (count === 1) {
    return (
      <div className={className} aria-busy="true" aria-label="Loading…">
        <SkeletonSingle variant={variant} width={width} height={height} />
      </div>
    );
  }

  return (
    <div
      className={cn(
        isRow
          ? "rounded-card overflow-hidden border border-gray-100 dark:border-border-strong"
          : isInline
            ? "flex flex-col gap-2"
            : "grid gap-4 grid-cols-1",
        className,
      )}
      aria-busy="true"
      aria-label="Loading…"
    >
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonSingle key={i} variant={variant} width={width} height={height} />
      ))}
    </div>
  );
}

SkeletonLoader.displayName = "SkeletonLoader";
